import { useState, useEffect, useRef } from "react";
import { listTexts, getSampleTexts, getTTSStatus, checkDictation, getDictationTTS, uploadTeacherAudio, uploadAudioFile, getTeacherAudio, getAudioFile, analyzeGrammar, getDictationHints } from "../lib/api";
import { Volume2, Play, Pause, RotateCcw, Check, ArrowLeft, Pen, Mic, Square, Settings, Upload, BookText, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function DictationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";

  const [texts, setTexts] = useState([]);
  const [samples, setSamples] = useState([]);
  const [selectedText, setSelectedText] = useState(null);
  const [typedText, setTypedText] = useState("");
  const [result, setResult] = useState(null);
  const [ttsAvailable, setTtsAvailable] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [step, setStep] = useState("select"); // select | dictation | results
  const [isRecording, setIsRecording] = useState(false);
  const [teacherAudioAvailable, setTeacherAudioAvailable] = useState(false);
  const [teacherAudioId, setTeacherAudioId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [ttsSettings, setTtsSettings] = useState({
    slowMode: true,
    announcePunctuation: true,
    pauseBetweenSentences: true,
    useTeacherAudio: true,
  });
  const [uploadingAudio, setUploadingAudio] = useState(false);
  
  // Grammar integration
  const [grammarAnalysis, setGrammarAnalysis] = useState(null);
  const [showGrammar, setShowGrammar] = useState(false);
  const [dictationHints, setDictationHints] = useState(null);
  const [showHints, setShowHints] = useState(false);
  const [grammarLevel, setGrammarLevel] = useState("intermediate");

  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    Promise.all([
      listTexts().catch(() => ({ data: [] })),
      getSampleTexts().catch(() => ({ data: [] })),
      getTTSStatus().catch(() => ({ data: { available: false } })),
    ]).then(([t, s, tts]) => {
      setTexts(t.data);
      setSamples(s.data);
      setTtsAvailable(tts.data.available);
    });
  }, []);

  // Check for teacher audio when text is selected
  useEffect(() => {
    if (selectedText?.id) {
      getTeacherAudio(selectedText.id)
        .then((res) => {
          setTeacherAudioAvailable(res.data.available);
          if (res.data.available) {
            setTeacherAudioId(res.data.audio_id);
          }
        })
        .catch(() => setTeacherAudioAvailable(false));
    }
  }, [selectedText?.id]);

  const playAudio = async () => {
    const text = selectedText?.text || selectedText?.raw_text || "";
    if (!text) return;

    setIsPlaying(true);
    try {
      let blob;
      
      // Try teacher audio first if available and enabled
      if (teacherAudioAvailable && ttsSettings.useTeacherAudio && teacherAudioId) {
        const res = await getAudioFile(teacherAudioId);
        blob = res.data;
      } else if (ttsAvailable) {
        // Use enhanced TTS
        const res = await getDictationTTS(text, {
          slowMode: ttsSettings.slowMode,
          announcePunctuation: ttsSettings.announcePunctuation,
          pauseBetweenSentences: ttsSettings.pauseBetweenSentences,
        });
        blob = res.data;
      } else {
        setIsPlaying(false);
        return;
      }

      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => setIsPlaying(false);
      await audio.play();
    } catch (err) {
      console.error("Audio playback failed:", err);
      setIsPlaying(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Teacher recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        await saveTeacherRecording(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Recording failed:", err);
      alert("Impossible d'acceder au microphone.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const saveTeacherRecording = async (blob) => {
    if (!selectedText?.id) return;
    setUploadingAudio(true);
    try {
      const res = await uploadTeacherAudio(selectedText.id);
      const audioId = res.data.audio_id;
      
      const file = new File([blob], "recording.webm", { type: "audio/webm" });
      await uploadAudioFile(audioId, file);
      
      setTeacherAudioAvailable(true);
      setTeacherAudioId(audioId);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleCheck = async () => {
    if (!selectedText || !typedText.trim()) return;
    try {
      const res = await checkDictation(selectedText.text || selectedText.raw_text, typedText);
      setResult(res.data);
      setStep("results");
      
      // Load grammar analysis for results
      const text = selectedText.text || selectedText.raw_text;
      analyzeGrammar(text, grammarLevel)
        .then((r) => setGrammarAnalysis(r.data))
        .catch(console.error);
    } catch (err) {
      console.error("Check failed:", err);
    }
  };

  const handleReset = () => {
    setTypedText("");
    setResult(null);
    setGrammarAnalysis(null);
    setShowGrammar(false);
    setStep("dictation");
  };

  // Load hints when entering dictation mode
  const loadHints = async () => {
    const text = selectedText?.text || selectedText?.raw_text;
    if (!text) return;
    try {
      const res = await getDictationHints(text, grammarLevel);
      setDictationHints(res.data);
    } catch (err) {
      console.error("Failed to load hints:", err);
    }
  };

  // Error type labels in French
  const errorLabels = {
    capitalization: "Majuscules",
    punctuation: "Ponctuation",
    spelling: "Orthographe",
    phonetic: "Phonetique",
    missing: "Mots manquants",
    extra: "Mots en trop",
  };

  const errorColors = {
    capitalization: "bg-yellow-100 text-yellow-700 border-yellow-300",
    punctuation: "bg-purple-100 text-purple-700 border-purple-300",
    spelling: "bg-red-100 text-red-700 border-red-300",
    phonetic: "bg-blue-100 text-blue-700 border-blue-300",
    missing: "bg-gray-100 text-gray-700 border-gray-300",
    extra: "bg-orange-100 text-orange-700 border-orange-300",
  };

  return (
    <div data-testid="dictation-page" className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8 page-enter has-bottom-nav lg:pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        {step !== "select" && (
          <button
            data-testid="dictation-back"
            onClick={() => {
              setStep("select");
              setResult(null);
              setTypedText("");
              stopAudio();
            }}
            className="p-2 rounded-xl text-slate-400 active:text-slate-700 active:bg-slate-100 touch-target"
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <Pen size={24} className="text-blue-500" />
        <h1 data-testid="dictation-title" className="font-fredoka text-3xl md:text-4xl font-bold text-slate-800 flex-1">
          {step === "select" ? "Dictee" : step === "results" ? "Resultats" : selectedText?.title || "Dictee"}
        </h1>
        {step === "dictation" && (
          <button
            data-testid="dictation-settings"
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-xl transition-colors touch-target ${showSettings ? "bg-blue-100 text-blue-600" : "text-slate-400 active:bg-slate-100"}`}
          >
            <Settings size={20} />
          </button>
        )}
      </div>

      {/* Step 1: Select text */}
      {step === "select" && (
        <div className="space-y-6 page-enter">
          <p className="font-nunito text-base text-slate-500">
            Choisis un texte, ecoute-le puis ecris ce que tu entends !
          </p>

          {!ttsAvailable && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-4 text-center">
              <p className="font-nunito font-bold text-sm text-orange-700">
                Audio synthetise non disponible - utilisez les enregistrements enseignant.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <h3 className="font-fredoka font-bold text-lg text-slate-700">Textes exemples</h3>
            {samples.map((s, i) => (
              <button
                key={i}
                data-testid={`dictation-sample-${i}`}
                onClick={() => {
                  setSelectedText({ ...s, id: `sample-${i}` });
                  setStep("dictation");
                }}
                className="w-full text-left bg-white border-2 border-slate-100 rounded-2xl p-4 active:border-blue-300 transition-all touch-target"
              >
                <p className="font-fredoka font-bold text-base text-slate-800">{s.title}</p>
                <p className="font-nunito text-sm text-slate-400 mt-1 line-clamp-1">{s.text}</p>
              </button>
            ))}
          </div>

          {texts.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-fredoka font-bold text-lg text-slate-700">Mes textes</h3>
              {texts.map((t) => (
                <button
                  key={t.id}
                  data-testid={`dictation-text-${t.id}`}
                  onClick={() => {
                    setSelectedText(t);
                    setStep("dictation");
                  }}
                  className="w-full text-left bg-white border-2 border-slate-100 rounded-2xl p-4 active:border-blue-300 transition-all touch-target"
                >
                  <p className="font-fredoka font-bold text-base text-slate-800">{t.title}</p>
                  <p className="font-nunito text-sm text-slate-400 mt-1 line-clamp-1">{t.raw_text}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Dictation */}
      {step === "dictation" && (
        <div className="space-y-4 page-enter">
          {/* Settings panel */}
          {showSettings && (
            <div className="bg-slate-50 rounded-2xl border-2 border-slate-200 p-4 space-y-3 page-enter">
              <h3 className="font-fredoka font-bold text-base text-slate-700">Options de lecture</h3>
              
              {teacherAudioAvailable && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ttsSettings.useTeacherAudio}
                    onChange={(e) => setTtsSettings({ ...ttsSettings, useTeacherAudio: e.target.checked })}
                    className="w-5 h-5 rounded text-blue-500"
                  />
                  <span className="font-nunito text-sm text-slate-600">
                    Utiliser l'enregistrement de l'enseignant
                  </span>
                </label>
              )}
              
              <div className={teacherAudioAvailable && ttsSettings.useTeacherAudio ? "opacity-50 pointer-events-none" : ""}>
                <p className="font-nunito text-xs text-slate-400 mb-2">Options TTS automatique :</p>
                <label className="flex items-center gap-3 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={ttsSettings.slowMode}
                    onChange={(e) => setTtsSettings({ ...ttsSettings, slowMode: e.target.checked })}
                    className="w-5 h-5 rounded text-blue-500"
                  />
                  <span className="font-nunito text-sm text-slate-600">Lecture lente</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={ttsSettings.announcePunctuation}
                    onChange={(e) => setTtsSettings({ ...ttsSettings, announcePunctuation: e.target.checked })}
                    className="w-5 h-5 rounded text-blue-500"
                  />
                  <span className="font-nunito text-sm text-slate-600">Annoncer la ponctuation</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ttsSettings.pauseBetweenSentences}
                    onChange={(e) => setTtsSettings({ ...ttsSettings, pauseBetweenSentences: e.target.checked })}
                    className="w-5 h-5 rounded text-blue-500"
                  />
                  <span className="font-nunito text-sm text-slate-600">Pauses entre phrases</span>
                </label>
              </div>
              
              {/* Grammar hints option */}
              <div className="pt-3 border-t border-slate-200">
                <p className="font-nunito text-xs text-slate-400 mb-2">Mode dictee guidee :</p>
                <label className="flex items-center gap-3 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={showHints}
                    onChange={(e) => {
                      setShowHints(e.target.checked);
                      if (e.target.checked && !dictationHints) loadHints();
                    }}
                    className="w-5 h-5 rounded text-purple-500"
                  />
                  <span className="font-nunito text-sm text-slate-600">
                    Afficher les indices grammaticaux
                  </span>
                </label>
                {showHints && (
                  <div className="flex gap-2 mt-2">
                    {["simple", "intermediate", "complete"].map((l) => (
                      <button
                        key={l}
                        onClick={() => { setGrammarLevel(l); loadHints(); }}
                        className={`px-3 py-1 rounded-lg text-xs font-nunito font-bold transition-all ${
                          grammarLevel === l
                            ? "bg-purple-500 text-white"
                            : "bg-purple-100 text-purple-600"
                        }`}
                      >
                        {l === "simple" ? "Simple" : l === "intermediate" ? "Inter." : "Complet"}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Grammar hints display */}
          {showHints && dictationHints && (
            <div className="bg-purple-50 rounded-2xl border-2 border-purple-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <BookText size={18} className="text-purple-500" />
                <h3 className="font-fredoka font-bold text-sm text-purple-700">
                  Structure grammaticale
                </h3>
              </div>
              {dictationHints.hints.map((hint, hi) => (
                <div key={hi} className="flex flex-wrap gap-1 mb-2">
                  {hint.word_hints.map((w, wi) =>
                    w.type === "space" ? (
                      <span key={wi} className="w-2" />
                    ) : w.type === "punct" ? (
                      <span key={wi} className="font-nunito text-sm text-slate-400">{w.text}</span>
                    ) : (
                      <span
                        key={wi}
                        className="px-2 py-0.5 rounded text-xs font-nunito"
                        style={{
                          backgroundColor: `${w.pos_color}20`,
                          color: w.pos_color,
                          borderBottom: `2px solid ${w.pos_color}`,
                        }}
                        title={`${w.pos_label} - ${w.length} lettres`}
                      >
                        {w.first_letter}{"_".repeat(Math.max(0, w.length - 1))}
                      </span>
                    )
                  )}
                </div>
              ))}
              <p className="font-nunito text-xs text-purple-500 mt-2">
                💡 Les couleurs indiquent la nature des mots. Survole pour plus de details.
              </p>
            </div>
          )}

          {/* Audio controls */}
          <div className="bg-white rounded-2xl border-2 border-blue-100 p-5">
            <div className="flex items-center gap-4">
              <button
                data-testid="dictation-play"
                onClick={isPlaying ? stopAudio : playAudio}
                disabled={!ttsAvailable && !teacherAudioAvailable}
                className="w-14 h-14 rounded-full bg-blue-500 active:bg-blue-600 text-white flex items-center justify-center shadow-[0_4px_0_rgb(29,78,216)] btn-bouncy flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPlaying ? <Pause size={24} /> : <Volume2 size={24} />}
              </button>
              <div className="flex-1">
                <p className="font-fredoka font-bold text-base text-slate-800">
                  {isPlaying ? "Lecture en cours..." : "Ecoute bien !"}
                </p>
                <p className="font-nunito text-sm text-slate-400">
                  {teacherAudioAvailable && ttsSettings.useTeacherAudio
                    ? "Voix de l'enseignant"
                    : "Voix synthetisee"}
                </p>
              </div>
              
              {/* Teacher recording controls */}
              {isTeacher && selectedText?.id && !selectedText.id.startsWith("sample-") && (
                <div className="flex items-center gap-2">
                  {isRecording ? (
                    <button
                      data-testid="dictation-stop-record"
                      onClick={stopRecording}
                      className="w-12 h-12 rounded-full bg-red-500 active:bg-red-600 text-white flex items-center justify-center shadow-[0_4px_0_rgb(185,28,28)] animate-pulse"
                    >
                      <Square size={20} />
                    </button>
                  ) : (
                    <button
                      data-testid="dictation-start-record"
                      onClick={startRecording}
                      disabled={uploadingAudio}
                      className="w-12 h-12 rounded-full bg-orange-500 active:bg-orange-600 text-white flex items-center justify-center shadow-[0_4px_0_rgb(194,65,12)] disabled:opacity-50"
                      title="Enregistrer ma voix"
                    >
                      {uploadingAudio ? (
                        <Upload size={20} className="animate-bounce" />
                      ) : (
                        <Mic size={20} />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {isRecording && (
              <div className="mt-3 flex items-center gap-2 text-red-600">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="font-nunito text-sm font-bold">Enregistrement en cours...</span>
              </div>
            )}
            
            {teacherAudioAvailable && isTeacher && (
              <p className="mt-3 font-nunito text-xs text-green-600">
                ✓ Enregistrement enseignant disponible
              </p>
            )}
          </div>

          {/* Text input */}
          <div>
            <label className="font-nunito font-bold text-sm text-slate-500 uppercase tracking-wider mb-2 block">
              Ecris ce que tu entends
            </label>
            <textarea
              data-testid="dictation-input"
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              placeholder="Ecris ici..."
              className="w-full min-h-[200px] bg-white border-3 border-orange-100 rounded-2xl p-5 text-lg font-lexend leading-relaxed focus:outline-none focus:border-orange-400 transition-colors placeholder:text-slate-300 shadow-inner resize-y"
            />
            <p className="font-nunito text-xs text-slate-400 mt-2">
              💡 Attention aux majuscules en debut de phrase et a la ponctuation !
            </p>
          </div>

          {/* Submit */}
          <button
            data-testid="dictation-check-btn"
            onClick={handleCheck}
            disabled={!typedText.trim()}
            className={`w-full py-4 rounded-full font-fredoka font-bold text-lg text-white btn-bouncy transition-all touch-target ${
              typedText.trim()
                ? "bg-orange-500 active:bg-orange-600 shadow-[0_4px_0_rgb(194,65,12)]"
                : "bg-slate-300 shadow-[0_4px_0_rgb(148,163,184)] cursor-not-allowed"
            }`}
          >
            <Check size={20} className="inline mr-2" />
            Verifier ma dictee
          </button>
        </div>
      )}

      {/* Step 3: Results */}
      {step === "results" && result && (
        <div className="space-y-6 page-enter">
          {/* Score */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center shadow-md">
            <img src="/LEcoleDeNavi4.png" alt="Navi" className="h-20 mx-auto mb-3 mascot-float" />
            <p
              className="font-fredoka font-bold text-4xl mb-1"
              style={{
                color: result.score >= 80 ? "#22C55E" : result.score >= 50 ? "#F97316" : "#EF4444",
              }}
            >
              {result.score}%
            </p>
            <p className="font-nunito text-base text-slate-500">
              {result.correct}/{result.total} mots corrects
            </p>
            <p className="font-nunito text-sm text-slate-400 mt-1">
              {result.score >= 80
                ? "Super travail !"
                : result.score >= 50
                ? "Bon effort, continue !"
                : "Continue a t'entrainer !"}
            </p>
          </div>

          {/* Error statistics */}
          {result.error_stats && (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-5 shadow-md">
              <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-3">
                Types d'erreurs
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(result.error_stats).map(([type, count]) => (
                  <div
                    key={type}
                    data-testid={`error-stat-${type}`}
                    className={`rounded-xl border-2 p-3 text-center ${
                      count > 0 ? errorColors[type] : "bg-green-50 text-green-600 border-green-200"
                    }`}
                  >
                    <p className="font-fredoka font-bold text-2xl">{count}</p>
                    <p className="font-nunito text-xs">{errorLabels[type]}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Word-by-word results */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-5 shadow-md">
            <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-3">Detail</h3>
            <div className="flex flex-wrap gap-2">
              {result.results.map((r, i) => (
                <div
                  key={i}
                  data-testid={`dictation-result-${i}`}
                  className={`px-3 py-1.5 rounded-xl font-nunito font-bold text-sm ${
                    r.correct
                      ? "bg-green-100 text-green-700"
                      : r.error_type
                      ? errorColors[r.error_type] || "bg-red-100 text-red-700"
                      : "bg-red-100 text-red-700"
                  }`}
                  title={r.error_type ? errorLabels[r.error_type] : ""}
                >
                  {r.correct ? (
                    r.original
                  ) : (
                    <span>
                      <span className="line-through opacity-50">{r.typed || "___"}</span>{" "}
                      <span className="text-green-600">{r.original}</span>
                    </span>
                  )}
                </div>
              ))}
            </div>
            
            {/* Legend */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="font-nunito text-xs text-slate-400 mb-2">Legende des couleurs :</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(errorLabels).map(([type, label]) => (
                  <span key={type} className={`px-2 py-1 rounded text-xs ${errorColors[type]}`}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              data-testid="dictation-retry"
              onClick={handleReset}
              className="flex-1 py-3 bg-blue-500 active:bg-blue-600 text-white font-fredoka font-bold rounded-full shadow-[0_4px_0_rgb(29,78,216)] btn-bouncy transition-all touch-target flex items-center justify-center gap-2"
            >
              <RotateCcw size={18} />
              Recommencer
            </button>
            <button
              data-testid="dictation-new"
              onClick={() => {
                setStep("select");
                setResult(null);
                setTypedText("");
                setSelectedText(null);
                setGrammarAnalysis(null);
                setShowGrammar(false);
              }}
              className="flex-1 py-3 bg-orange-500 active:bg-orange-600 text-white font-fredoka font-bold rounded-full shadow-[0_4px_0_rgb(194,65,12)] btn-bouncy transition-all touch-target"
            >
              Autre texte
            </button>
          </div>

          {/* Grammar analysis toggle */}
          <button
            data-testid="show-grammar-btn"
            onClick={() => setShowGrammar(!showGrammar)}
            className={`w-full py-3 rounded-full font-fredoka font-bold text-sm transition-all touch-target flex items-center justify-center gap-2 ${
              showGrammar
                ? "bg-purple-500 text-white shadow-[0_4px_0_rgb(126,34,206)]"
                : "bg-purple-100 text-purple-700 active:bg-purple-200"
            }`}
          >
            <BookText size={18} />
            {showGrammar ? "Masquer l'analyse grammaticale" : "Voir l'analyse grammaticale"}
          </button>

          {/* Grammar analysis display */}
          {showGrammar && grammarAnalysis && (
            <div className="bg-white rounded-2xl border-2 border-purple-200 p-5 shadow-md page-enter">
              <h3 className="font-fredoka font-bold text-lg text-purple-800 mb-4 flex items-center gap-2">
                <BookText size={20} className="text-purple-500" />
                Analyse grammaticale
              </h3>
              
              {grammarAnalysis.sentences.map((sent, si) => (
                <div key={si} className="mb-4 pb-4 border-b border-purple-100 last:border-0 last:pb-0 last:mb-0">
                  <div className="flex flex-wrap gap-1 mb-3">
                    {sent.tokens.map((token, ti) =>
                      token.is_space ? (
                        <span key={ti} className="w-2" />
                      ) : token.is_punct ? (
                        <span key={ti} className="font-lexend text-lg text-slate-400">{token.text}</span>
                      ) : (
                        <span
                          key={ti}
                          className="relative group cursor-help"
                        >
                          <span
                            className="font-lexend text-lg px-1 py-0.5 rounded"
                            style={{
                              backgroundColor: `${token.pos_color}20`,
                              color: token.pos_color,
                              borderBottom: `2px solid ${token.pos_color}`,
                            }}
                          >
                            {token.text}
                          </span>
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                            {token.pos_label} ({token.dep_label})
                          </span>
                        </span>
                      )
                    )}
                  </div>
                  
                  {/* Structure groups */}
                  {sent.structure?.groups?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {sent.structure.groups.map((group, gi) => (
                        <div
                          key={gi}
                          className="px-2 py-1 rounded-lg border text-xs"
                          style={{
                            borderColor: group.color,
                            backgroundColor: `${group.color}10`,
                            color: group.color,
                          }}
                        >
                          <span className="font-bold">{group.label}:</span> {group.tokens.join(" ")}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Stats */}
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-purple-100">
                {Object.entries(grammarAnalysis.stats || {}).map(([label, count]) => (
                  <span key={label} className="px-2 py-1 bg-purple-50 text-purple-600 rounded text-xs font-nunito">
                    {label} ({count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
